import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Link,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNavigate } from "react-router-dom";
import PageLayout from "../../components/PageLayout";
import CommonPagination from "../../components/Table/CommonPagination";
import { showToast } from "../../store/slices/common";
import { useDispatch } from "react-redux";
import { listSales } from "../../http/sales";

const PAGE_SIZE = 20;

const KIND_TABS = [
  { value: "all", label: "All" },
  { value: "vote_button", label: "Vote buttons" },
  { value: "live_gift", label: "Live gifts" },
];

const KIND_CHIP = {
  vote_button: { label: "Vote button", color: "primary" },
  live_gift: { label: "Live gift", color: "secondary" },
};

const PLATFORM_LABEL = {
  ios: "iOS",
  android: "Android",
};

const headCellSx = {
  fontWeight: 700,
  color: "#142C5C",
  backgroundColor: "#f4f6fb",
  fontSize: "13px",
  whiteSpace: "nowrap",
};

const cardSx = {
  padding: "16px 18px",
  borderRadius: "10px",
  boxShadow: "rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px",
  minWidth: 140,
  flex: "1 1 160px",
};

const emptySummary = {
  count: 0,
  amountCents: 0,
  voteButtons: { count: 0, amountCents: 0 },
  liveGifts: { count: 0, amountCents: 0 },
};

const formatMoney = (cents) => {
  const value = Number(cents);
  if (!Number.isFinite(value)) return "—";
  return `$${(value / 100).toFixed(2)}`;
};

const formatWhen = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

const tabCount = (summary, kind) => {
  if (kind === "vote_button") return summary.voteButtons.count;
  if (kind === "live_gift") return summary.liveGifts.count;
  return summary.count;
};

const visibleRevenue = (summary, kind) => {
  if (kind === "vote_button") return summary.voteButtons.amountCents;
  if (kind === "live_gift") return summary.liveGifts.amountCents;
  return summary.amountCents;
};

const UserCell = ({ user, onOpen }) => {
  if (!user?._id && !user?.username) {
    return <Typography sx={{ fontSize: "13px", color: "#888" }}>Unknown user</Typography>;
  }
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
      <Avatar src={user.profilePicture || undefined} sx={{ width: 28, height: 28, fontSize: 13 }}>
        {(user.username || "?").slice(0, 1).toUpperCase()}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        {user._id ? (
          <Link
            component="button"
            underline="hover"
            onClick={() => onOpen(user._id)}
            sx={{ fontSize: "13px", fontWeight: 600, color: "#142C5C" }}
          >
            {user.username || "Unnamed"}
          </Link>
        ) : (
          <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>{user.username}</Typography>
        )}
        {user.email && (
          <Typography sx={{ fontSize: "12px", color: "#777", lineHeight: 1.3 }}>
            {user.email}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const Sales = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [kind, setKind] = useState("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(emptySummary);
  const [loading, setLoading] = useState(true);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(0);
  }, [kind, debouncedSearch]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const response = await listSales({
          page: page + 1,
          limit: PAGE_SIZE,
          kind,
          search: debouncedSearch,
        });
        if (cancelled) return;
        const data = response?.data || {};
        setRows(data.list || []);
        setTotal(data.resultCount || 0);
        setSummary({ ...emptySummary, ...(data.summary || {}) });
      } catch (error) {
        if (!cancelled) {
          dispatch(
            showToast({
              color: "error",
              msg: error?.response?.data?.message || "Failed to load sales.",
            })
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [page, kind, debouncedSearch, refreshToken, dispatch]);

  const openUser = (userId) => navigate(`/manage-users/view-user/${userId}`);
  const revenue = visibleRevenue(summary, kind);
  const saleCount = kind === "all" ? summary.count : tabCount(summary, kind);

  return (
    <PageLayout
      title="Sales"
      subtitle="Completed purchases: who bought, when, and which button."
      loading={loading && rows.length === 0}
      actions={
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => setRefreshToken((n) => n + 1)}
          sx={{ textTransform: "none", color: "#142C5C", borderColor: "#d5d9e6" }}
        >
          Refresh
        </Button>
      }
      search={
        <TextField
          size="small"
          placeholder="Search user, button, or transaction"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ minWidth: { xs: "100%", sm: 320 } }}
        />
      }
    >
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2.5 }}>
        <Box sx={cardSx}>
          <Typography sx={{ fontSize: "12px", color: "#777" }}>Purchases</Typography>
          <Typography sx={{ fontSize: "22px", fontWeight: 700, color: "#142C5C" }}>
            {saleCount}
          </Typography>
        </Box>
        <Box sx={cardSx}>
          <Typography sx={{ fontSize: "12px", color: "#777" }}>Revenue</Typography>
          <Typography sx={{ fontSize: "22px", fontWeight: 700, color: "#142C5C" }}>
            {formatMoney(revenue)}
          </Typography>
        </Box>
        <Box sx={cardSx}>
          <Typography sx={{ fontSize: "12px", color: "#777" }}>Vote buttons</Typography>
          <Typography sx={{ fontSize: "22px", fontWeight: 700, color: "#142C5C" }}>
            {formatMoney(summary.voteButtons.amountCents)}
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "#777" }}>
            {summary.voteButtons.count} purchase{summary.voteButtons.count === 1 ? "" : "s"}
          </Typography>
        </Box>
        <Box sx={cardSx}>
          <Typography sx={{ fontSize: "12px", color: "#777" }}>Live gifts</Typography>
          <Typography sx={{ fontSize: "22px", fontWeight: 700, color: "#142C5C" }}>
            {formatMoney(summary.liveGifts.amountCents)}
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "#777" }}>
            {summary.liveGifts.count} purchase{summary.liveGifts.count === 1 ? "" : "s"}
          </Typography>
        </Box>
      </Box>

      <Tabs
        value={kind}
        onChange={(_, value) => setKind(value)}
        sx={{ mb: 2, minHeight: 40 }}
      >
        {KIND_TABS.map((tab) => (
          <Tab
            key={tab.value}
            value={tab.value}
            label={`${tab.label} (${tabCount(summary, tab.value)})`}
            sx={{ textTransform: "none", minHeight: 40 }}
          />
        ))}
      </Tabs>

      <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e6e8f0" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>When</TableCell>
              <TableCell sx={headCellSx}>User</TableCell>
              <TableCell sx={headCellSx}>Button</TableCell>
              <TableCell sx={headCellSx}>Amount</TableCell>
              <TableCell sx={headCellSx}>Platform</TableCell>
              <TableCell sx={headCellSx}>Sent to</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: "center", py: 6, color: "#777" }}>
                  {debouncedSearch ? "No purchases match this search." : "No purchases yet."}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const chip = KIND_CHIP[row.kind] || { label: row.kind, color: "default" };
                return (
                  <TableRow key={row._id} hover>
                    <TableCell sx={{ whiteSpace: "nowrap", fontSize: "13px" }}>
                      {formatWhen(row.purchasedAt)}
                    </TableCell>
                    <TableCell>
                      <UserCell user={row.user} onOpen={openUser} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                          {row.button?.emoji ? `${row.button.emoji} ` : ""}
                          {row.button?.title || "—"}
                        </Typography>
                        <Chip size="small" label={chip.label} color={chip.color} variant="outlined" />
                      </Box>
                      {row.transactionId && (
                        <Typography sx={{ fontSize: "11px", color: "#999", mt: 0.25 }}>
                          {row.transactionId}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: "13px", fontWeight: 600, whiteSpace: "nowrap" }}>
                      {formatMoney(row.amountCents)}
                    </TableCell>
                    <TableCell sx={{ fontSize: "13px", whiteSpace: "nowrap" }}>
                      {PLATFORM_LABEL[row.platform] || row.platform || "—"}
                    </TableCell>
                    <TableCell>
                      {row.recipient ? (
                        <UserCell user={row.recipient} onOpen={openUser} />
                      ) : (
                        <Typography sx={{ fontSize: "13px", color: "#aaa" }}>—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {total > PAGE_SIZE && (
        <Box sx={{ mt: 2 }}>
          <CommonPagination
            rowsPerPage={PAGE_SIZE}
            count={total}
            page={page}
            onPageChange={(_, nextPage) => setPage(nextPage)}
          />
        </Box>
      )}
    </PageLayout>
  );
};

export default Sales;
